import * as React from "react";
import CalendarIcon from "../icons/Calendar";
import enAU from "date-fns/locale/en-AU";
import formatRelative from "date-fns/formatRelative";
import parse from "date-fns/parse";
import { add } from "date-fns";
import { Block } from "../Block";
import { capitalize } from "../utils";
import { expandBorderRadius, expandPadding } from "../utils/styles";
import { getInputOverrides, Input } from "../Input";
import { isEmpty, isObject } from "../utils/primitives";
import { MaskedInput as BaseMaskedInput } from "baseui/input";
import { mergeOverrides } from "baseui/helpers/overrides";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useToggle } from "../hooks/toggle";
/**
 * DatePicker widget.
 *
 * TODO: There's still a little bug in here that's related to the way the masking/human input works. Because they're
 * swapped out, this causes the end-enhancer to be replaced. This results in any clicks on the end-enhancer while
 * focused on the MaskInput, that click is lost. The long-term solution is to do one of two things, I think:
 *
 *  * Don't use an end-enhancer (i.e. render outside the Inputs so it doesn't get replaced).
 *  * Don't swap the inputs.
 */

import {
  DatePicker as BaseDatePicker,
  Calendar as BaseCalendar,
  DatepickerProps as BaseDatepickerProps,
  CalendarProps,
} from "baseui/datepicker";

/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-empty */
// TODO: Enable the proptype check next time when this file is changed

// Modify the start of week
enAU.options.weekStartsOn = 1;

type DatepickerProps = BaseDatepickerProps & {
  onFocus?: () => void;
  onBlur?: Function;
  placeholder?: string;
  name?: string;
  defaultValue?: unknown;
  validators?: Array<{
    type: "DateRangeValidator";
    options: {
      givenRange: {
        years: number;
      };
      limitType: "min" | "max";
    };
  }>;
  formatRelative?: boolean;
};

function DatePicker(props: DatepickerProps) {
  const {
    overrides: pickerOverrides,
    onChange = () => {},
    value,
    defaultValue,
    minDate: minDateProp,
    maxDate,
    disabled,
    formatString = "dd/MM/yyyy",
    mask = "99/99/9999",
    validators,
    formatRelative = true,
    ...restProps
  } = props;
  const [isFocused, setIsFocused] = React.useState(false);
  const [isCalendarOpen, toggleCalendarOpen] = useToggle(false);
  function handleChange({ date }) {
    onChange(date);
  }
  React.useEffect(() => {
    if (defaultValue && !value) {
      onChange(makeDate(defaultValue));
    }
  }, []);

  const minValidator = validators?.find((validator) => {
    return validator.options?.limitType === "min";
  });

  const minDate = minDateProp ?? minValidator?.options?.givenRange;

  return (
    <BaseDatePicker
      overrides={mergeOverrides(
        getDatePickerOverrides({
          ...restProps,
          formatString,
          isFocused,
          setIsFocused,
          isCalendarOpen,
          toggleCalendarOpen,
          disabled,
          formatRelative,
        }),
        pickerOverrides
      )}
      placeholder={null}
      onChange={handleChange}
      minDate={makeDate(minDate)}
      maxDate={makeDate(maxDate)}
      value={makeDate(value)}
      locale={enAU}
      formatString={formatString}
      mask={mask}
      disabled={disabled}
      {...restProps}
    />
  );
}

function HumanInput(props) {
  const {
    value,
    hooks = {},
    onFocus,
    onBlur,
    formatString,
    setIsFocused,
    formatRelative,
    ...restProps
  } = props;
  return (
    <Input
      value={formatRelative ? humanify(value, formatString) : value}
      onFocus={(...args) => {
        setIsFocused(true);
        onFocus(...args);
        hooks.onFocus(...args);
      }}
      onBlur={(...args) => {
        onBlur(...args);
        hooks.onBlur(...args);
      }}
      {...restProps}
    />
  );
}

function MaskedInput({
  hooks: { onBlur: hookOnBlur = () => {}, onFocus: hookOnFocus = () => {} },
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  setIsFocused,
  setIsEmpty,
  toggleCalendarOpen,
  inputRef,
  value,
  size,
  ...restProps
}) {
  const ref = React.useRef(inputRef);

  React.useEffect(() => {
    ref.current?.focus();
  }, [ref.current]);

  return (
    <BaseMaskedInput
      {...restProps}
      overrides={getInputOverrides({ size })}
      onFocus={(...args) => {
        onFocus(...args);
        hookOnFocus(...args);
      }}
      onBlur={(...args) => {
        onBlur(...args);
        setIsFocused(false);
        toggleCalendarOpen(false);
        hookOnBlur(...args);
      }}
      onChange={(event) => {
        setIsEmpty(isEmpty(value));
        onChange(event);
      }}
      inputRef={ref}
    />
  );
}

function makeDate(value) {
  if (value === "today") {
    value = new Date();
  } else if (value === "tomorrow") {
    value = add(new Date(), { days: 1 });
  } else if (isObject(value)) {
    value = add(new Date(), value);
  }

  if (value && !(value instanceof Date)) {
    value = new Date(value);
  }
  return value;
}

function humanify(value, formatString) {
  if (value) {
    try {
      const result = formatRelative(parse(value, formatString, new Date()), new Date());
      const match = result.match(/(.+)( at .+)/);
      if (match) {
        return capitalize(match[1]);
      }
    } catch (e) {}
    return value;
  }
  return value;
}

function Calendar(props: CalendarProps) {
  const { overrides: calendarOverrides, ...restProps } = props;
  const overrides = mergeOverrides(getCalendarOverrides(), calendarOverrides);
  return <BaseCalendar overrides={overrides} {...restProps} />;
}

function getDatePickerOverrides(args = {}) {
  const {
    name,
    isFocused,
    onFocus,
    onBlur,
    inputRef,
    formatString,
    setIsFocused,
    setIsEmpty,
    placeholder = "",
    isCalendarOpen,
    toggleCalendarOpen,
    disabled,
    formatRelative,
  } = args;
  return {
    ...getCalendarOverrides(),
    Popover: {
      props: {
        isOpen: isCalendarOpen,
        onClickOutside: () => toggleCalendarOpen(false),
      },
    },
    Input: {
      component: isFocused ? MaskedInput : HumanInput,
      props: {
        formatRelative,
        hooks: {
          onFocus,
          onBlur,
        },
        inputRef,
        placeholder,
        name,
        formatString,
        isFocused,
        setIsFocused,
        setIsEmpty,
        toggleCalendarOpen,
        endEnhancer: !disabled ? (
          <CalendarEnhancer onClick={toggleCalendarOpen} />
        ) : undefined,
      },
    },
  };
}

function CalendarEnhancer(props) {
  const { onClick } = props;
  const [css] = useStyletron();
  // TODO: How do we properly handle the TS failure with onClick on
  // the Block? Use a Button instead? Or a link?
  return (
    <Block
      display="flex"
      className={css({ cursor: "pointer" })}
      onClick={onClick}
      role="button"
    >
      <CalendarIcon color="currentColor" />
    </Block>
  );
}

function getCalendarOverrides() {
  return {
    MonthHeader: {
      style: {
        height: "25px",
      },
    },
    WeekdayHeader: {
      style: {
        width: "35px",
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    Day: {
      style: ({ $theme, ...stateProps }) => {
        const {
          $disabled,
          $selected,
          $outsideMonth,
          $isHighlighted,
          $pseudoSelected,
          $pseudoHighlighted,
          $isHovered,
        } = stateProps;
        let backgroundColor = $theme.colors.backgroundPrimary;
        if ($disabled || $outsideMonth) {
          backgroundColor = $theme.colors.backgroundStateDisabled;
        }
        if ($selected) {
          backgroundColor = $theme.colors.backgroundAccent;
        }
        return {
          backgroundColor,
          color: $selected
            ? $theme.colors.contentInversePrimary
            : $theme.colors.contentSecondary,
          ...expandBorderRadius("2px"),
          ...expandPadding("4px"),
          width: "33px",
          height: "33px",
          marginRight: "1px",
          marginLeft: "1px",
          ":after": "none",
        };
      },
    },
    Week: {
      style: {
        marginBottom: "2px",
      },
    },
  };
}

export { DatePicker, Calendar };
